import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';

import { runBackHandlers } from '@/lib/backButton';
import { isStoreBuild } from '@/lib/platform';

/**
 * Аппаратная кнопка «Назад» на Android.
 *
 * Разбирает нажатие по слоям, сверху вниз: что открылось последним — то и
 * закрывается первым. До этого обработки не было вообще, и нажатие на любом
 * экране закрывало приложение целиком.
 *
 *   1. Модальное окно или вопрос — их обработчики лежат в стеке `backButton`;
 *      туда же встают экраны с таймером, которым нужно спросить перед выходом.
 *   2. Полноэкранное видео — выходим из полного экрана, а не из приложения.
 *   3. Раздел меню — возвращаемся на главный экран роли.
 *   4. Главный экран — спрашиваем окном (решение заказчика: не двойное нажатие).
 *   5. Всё остальное — обычный шаг назад по истории.
 *
 * На iPhone аппаратной кнопки нет, жестом занимается система, поэтому слушатель
 * ставится только в сборке для магазина и молчит везде, кроме Android.
 */
export function useAndroidBack(base: string, sectionPaths: string[], onExitRequest: () => void): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isStoreBuild()) return;

    let remove: (() => void) | undefined;
    let cancelled = false;

    void CapacitorApp.addListener('backButton', () => {
      // 1. Открытое поверх — модалка, вопрос о выходе из теста.
      if (runBackHandlers()) return;

      // 2. Полноэкранное видео: выходим из него, экран остаётся тем же.
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => {});
        return;
      }

      const path = location.pathname.replace(/\/+$/, '') || '/';

      // 3. Главный экран роли — дальше идти некуда, спрашиваем про выход.
      if (path === base) {
        onExitRequest();
        return;
      }

      // 4. Раздел меню — возвращаем на главный, а не в случайное место истории.
      //    Человек всегда знает, куда его вернёт кнопка.
      if (sectionPaths.includes(path)) {
        navigate(base);
        return;
      }

      // 5. Внутренний экран — шаг назад туда, откуда пришли.
      navigate(-1);
    }).then((handle) => {
      if (cancelled) void handle.remove();
      else remove = () => void handle.remove();
    });

    return () => {
      cancelled = true;
      remove?.();
    };
  }, [base, sectionPaths, onExitRequest, navigate, location.pathname]);
}
